import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';
import { useMemo, useRef } from 'react';

import { useIntegrationCRMProviders } from '@/api/integration';
import {
  type InstanceCampaignSettingsType,
  type InstanceDetailDataType,
  type InstanceLeadSettingsType,
  type InstanceTaskEventSettingsType,
} from '@/api/organization';
import { LinkButton } from '@/components/buttons';
import { Switch, useTypedAppFormContext } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';
import InfoPopover from '@/components/InfoPopover';
import { Spinner } from '@/components/loaders';
import { Results } from '@/components/Results';
import { SupportLink, Text } from '@/components/typography';
import { ROUTES } from '@/router';

import CompanyNonDigitalTasksList from './CompanyNonDigitalTasksList';
import CRMFieldCombobox from './CRMFieldCombobox';
import EmptyField from './EmptyField';

type Props = {
  isEditMode: boolean;
  instance?: InstanceDetailDataType;
};

const CompanyNonDigitalEventsSettings = ({ isEditMode, instance }: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });
  const {
    hasHubspotConnection,
    hasSalesforceConnection,
    isLoading: isCRMProvidersLoading,
    error: crmProvidersError,
  } = useIntegrationCRMProviders();
  const hasActiveCRMConnection = hasSalesforceConnection || hasHubspotConnection;
  const taskEventValue = useStore(form.store, (state) => state.values.taskEvent);
  const leadValue = useStore(form.store, (state) => state.values.lead);
  const campaignValue = useStore(form.store, (state) => state.values.campaign);

  const cachedTaskEventValue = useRef<InstanceTaskEventSettingsType>(undefined);
  const cachedLeadValue = useRef<InstanceLeadSettingsType>(undefined);
  const cachedCampaignValue = useRef<InstanceCampaignSettingsType>(undefined);

  const handleTasksToggleChange = (checked: boolean) => {
    form.setFieldValue(
      'taskEvent',
      checked
        ? (cachedTaskEventValue.current ?? {
            offlineEventsAll: false,
            offlineTasksAll: false,
            offlineTasksSpecific: [],
          })
        : null,
    );

    if (!checked) {
      cachedTaskEventValue.current = taskEventValue ?? undefined;
    }
  };

  const handleLeadsToggleChange = (checked: boolean) => {
    form.setFieldValue(
      'lead',
      checked
        ? (cachedLeadValue.current ?? {
            offlineEventsLeadStatus: '',
          })
        : null,
    );

    if (!checked) {
      cachedLeadValue.current = leadValue ?? undefined;
    }
  };

  const handleCampaignsToggleChange = (checked: boolean) => {
    form.setFieldValue(
      'campaign',
      checked
        ? (cachedCampaignValue.current ?? {
            spendField: '',
            typeField: '',
          })
        : null,
    );

    if (!checked) {
      cachedCampaignValue.current = campaignValue ?? undefined;
    }
  };

  const hasCRMFieldsError = useMemo(() => {
    if (!instance) {
      return false;
    }

    return (
      instance.leadStatusOptions.length === 0 &&
      instance.taskTypeOptions.length === 0 &&
      instance.typeFieldOptions.length === 0 &&
      instance.spendFieldOptions.length === 0
    );
  }, [instance]);

  if (isCRMProvidersLoading) {
    return <Spinner variant="spoke" />;
  }

  if (crmProvidersError) {
    return (
      <Results subtitle={<Trans>Error loading CRM provider data, please try again later.</Trans>} />
    );
  }

  if (!hasActiveCRMConnection) {
    return (
      <Results
        subtitle={
          <div className="flex flex-col items-center">
            <span>
              <Trans>A CRM connection is required for Non-Digital Events reporting.</Trans>
            </span>
            <span>
              <Trans>Please connect your CRM to activate this feature.</Trans>
            </span>
          </div>
        }
        extra={
          <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
            <Trans>Connect CRM</Trans>
          </LinkButton>
        }
      />
    );
  }

  if (hasSalesforceConnection && hasCRMFieldsError) {
    return (
      <Results
        subtitle={
          <div className="flex flex-col items-center">
            <span>
              <Trans>
                Your CRM is successfully connected however the user does not have access to required
                fields or no data exists.
              </Trans>
            </span>
            <span>
              <Trans>
                Please ensure the connected user has read-only access to the{' '}
                <SupportLink color="aqua" path="INTEGRATION_SALESFORCE">
                  required fields
                </SupportLink>
                .
              </Trans>
            </span>
          </div>
        }
        extra={
          <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
            <Trans>Review Integration</Trans>
          </LinkButton>
        }
      />
    );
  }

  if (!hasSalesforceConnection && hasHubspotConnection) {
    return (
      <Text variant="body2" weight="extra-light">
        <Trans>
          Configuration of Non-Digital Events is not necessary for HubSpot. When HubSpot is
          connected to Channel99, all campaign events will automatically be ingested into the
          platform within a few hours.
        </Trans>
      </Text>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-2">
      <form.AppField name="taskEvent">
        {(field) => (
          <field.Field className="lg:col-span-2">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <FieldLabel>
                  <Trans>Tasks & Events Reporting</Trans>
                </FieldLabel>
                {isEditMode && (
                  <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                    {({ isSubmitting }) => (
                      <Switch
                        aria-label={t`Include tasks and events`}
                        disabled={isSubmitting}
                        checked={field.state.value != null}
                        onCheckedChange={handleTasksToggleChange}
                      />
                    )}
                  </form.Subscribe>
                )}
                <InfoPopover
                  title={t`Tasks & Events Reporting`}
                  body={
                    <Trans>
                      Tasks and Events reporting originate primarily from sales tools automatically
                      logging calls and emails as well as manual logging within the Salesforce
                      account page. Unchecked types will be removed from company activity timelines
                      and all attribution calculations. Toggling "Off" will remove all tasks and
                      events from our system.
                    </Trans>
                  }
                />
              </div>
            </div>
            <CompanyNonDigitalTasksList
              taskOptions={instance?.taskTypeOptions}
              isEditMode={isEditMode}
              isHidden={field.state.value == null}
              value={field.state.value}
              onChange={field.handleChange}
            />
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      <form.AppField name="lead.offlineEventsLeadStatus">
        {(field) => (
          <field.Field className="lg:col-start-1">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor="company-settings-lead-status">
                  <Trans>Lead Status Tracking</Trans>
                </FieldLabel>
                {isEditMode && (
                  <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                    {({ isSubmitting }) => (
                      <Switch
                        aria-label={t`Include leads`}
                        disabled={isSubmitting}
                        checked={field.state.value != null}
                        onCheckedChange={handleLeadsToggleChange}
                      />
                    )}
                  </form.Subscribe>
                )}
                <InfoPopover
                  title={t`Lead Status Tracking`}
                  body={
                    <Trans>
                      Select the stage you would like to use to start tracking leads. This is
                      usually the stage you consider the lead an MQL. If this stage is skipped
                      within a lead status, we will still apply the progressing stages date so the
                      lead is not missed. Toggling "Off" will remove all lead data from our system.
                    </Trans>
                  }
                />
              </div>
            </div>
            {isEditMode ? (
              <CRMFieldCombobox
                className="max-w-90"
                placeholder={t`Select a Lead Status value`}
                items={instance?.leadStatusOptions}
                notFoundContent={t`No lead stages were found.`}
                disabled={field.state.value == null}
                aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                value={field.state.value ?? ''}
                onValueChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            ) : (
              <EmptyField
                options={instance?.leadStatusOptions}
                value={field.state.value ?? ''}
                showAsEmpty={field.state.value == null}
              />
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      <form.AppField name="campaign.typeField">
        {(field) => (
          <field.Field className="lg:col-start-1">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <FieldLabel htmlFor="company-settings-campaign-field">
                  <Trans>Campaign Field</Trans>
                </FieldLabel>
                {isEditMode && (
                  <form.Subscribe selector={(state) => ({ isSubmitting: state.isSubmitting })}>
                    {({ isSubmitting }) => (
                      <Switch
                        aria-label={t`Include campaigns`}
                        disabled={isSubmitting}
                        checked={field.state.value != null}
                        onCheckedChange={handleCampaignsToggleChange}
                      />
                    )}
                  </form.Subscribe>
                )}
                <InfoPopover
                  title={t`Campaign Field`}
                  body={
                    <Trans>
                      Choose the field used to categorize your campaigns. This field often includes
                      Field Events, Webinars, Email Campaigns and so on. Toggling "Off" will remove
                      campaign data from our system.
                    </Trans>
                  }
                />
              </div>
            </div>
            {isEditMode ? (
              <CRMFieldCombobox
                className="max-w-90"
                placeholder={t`Select a Campaign Field`}
                items={instance?.typeFieldOptions}
                notFoundContent={t`No campaign fields were found.`}
                disabled={field.state.value == null}
                aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                value={field.state.value ?? ''}
                onValueChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            ) : (
              <EmptyField
                options={instance?.typeFieldOptions}
                value={field.state.value ?? ''}
                showAsEmpty={field.state.value == null}
              />
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
      <form.AppField name="campaign.spendField">
        {(field) => (
          <field.Field className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <FieldLabel htmlFor="company-settings-campaign-spend-field">
                <Trans>Campaign Spend Field</Trans>
              </FieldLabel>
              <InfoPopover
                title={t`Campaign Spend Field`}
                body={
                  <Trans>
                    Choose the field which reflects where your organization logs cost data. This
                    will be used in ROI reporting.
                  </Trans>
                }
              />
            </div>
            {isEditMode ? (
              <CRMFieldCombobox
                className="max-w-90"
                placeholder={t`Select a Campaign Spend Field`}
                items={instance?.spendFieldOptions}
                notFoundContent={t`No campaign spend fields were found.`}
                disabled={field.state.value == null}
                aria-invalid={field.state.meta.isTouched && !field.state.meta.isValid}
                value={field.state.value ?? ''}
                onValueChange={field.handleChange}
                onBlur={field.handleBlur}
              />
            ) : (
              <EmptyField
                options={instance?.spendFieldOptions}
                value={field.state.value ?? ''}
                showAsEmpty={field.state.value == null}
              />
            )}
            <field.Error />
          </field.Field>
        )}
      </form.AppField>
    </div>
  );
};

export default CompanyNonDigitalEventsSettings;
